import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';

export interface BookingScheduleTimeWindow {
  startTime: string;
  endTime: string;
}

export interface BookingScheduleSetting {
  ID: string;
  hubID: number;
  hubName: string;
  // Null on every hub configured before the position was carried through.
  hubAddress: string | null;
  hubLatitude: string | null;
  hubLongitude: string | null;
  activeDays: number[];
  timeWindows: BookingScheduleTimeWindow[];
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface BookingScheduleSettingsRequest {
  size?: string;
  page?: string;
  order?: 'asc' | 'desc';
  sort?: string;
  isActive?: boolean;
  hubIDs?: string;
  search?: string;
  activeDays?: string;
}

export interface BookingScheduleSettingsResponse {
  status: number;
  data: {
    data: BookingScheduleSetting[];
    pagination: {
      total: number;
      size: number;
      page: number;
      lastPage: number;
    };
  };
  pagination?: {
    total: number;
    size: number;
    page: number;
    lastPage: number;
  };
}

export const getBookingScheduleSettings = async (
  params?: BookingScheduleSettingsRequest
): Promise<BookingScheduleSettingsResponse> => {
  try {
    const response = await Axios.get('/v1/fleet/setting/handover-booking-date', {
      params,
    });
    return response.data;
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export interface CreateBookingScheduleRequest {
  hubID: number;
  hubName: string;
  // Copied from the pitstop the user picked, so ops never types coordinates core
  // already holds. Optional because the edit modal has a fallback path with no
  // pitstop object to read them from — the backend leaves the stored position
  // alone when they are absent rather than clearing it.
  hubAddress?: string;
  hubLatitude?: string;
  hubLongitude?: string;
  activeDays: number[];
  timeWindows: BookingScheduleTimeWindow[];
  isActive: boolean;
  createdBy: string;
}

export const createBookingScheduleSetting = async (
  data: CreateBookingScheduleRequest
): Promise<{ status: number }> => {
  try {
    const response = await Axios.post('/v1/fleet/setting/handover-booking-date', data);
    return response.data;
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export interface UpdateBookingScheduleRequest {
  hubID: number;
  hubName: string;
  // Copied from the pitstop the user picked, so ops never types coordinates core
  // already holds. Optional because the edit modal has a fallback path with no
  // pitstop object to read them from — the backend leaves the stored position
  // alone when they are absent rather than clearing it.
  hubAddress?: string;
  hubLatitude?: string;
  hubLongitude?: string;
  activeDays: number[];
  timeWindows: BookingScheduleTimeWindow[];
  isActive: boolean;
  updatedBy: string;
}

export const updateBookingScheduleSetting = async (
  id: string,
  data: UpdateBookingScheduleRequest
): Promise<{ status: number }> => {
  try {
    const response = await Axios.patch(`/v1/fleet/setting/handover-booking-date/${id}`, data);
    return response.data;
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};
